import { useEffect } from 'react'
import { SITE_NAME, buildPageTitle } from './pageTitle'

type JsonLd = Record<string, unknown>

export interface SEOHeadProps {
  title: string
  description: string
  canonicalPath?: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'profile'
  jsonLd?: JsonLd | JsonLd[]
  /** Emit `robots: noindex, nofollow` — for pages that must stay out of
   *  search results (e.g. legal drafts pending final lawyer review). */
  noindex?: boolean
  /** The name this page belongs to, when it is not ours: a Club Storefront passes the
   *  club's own name and gets it in the tab title and `og:site_name`. Omitted
   *  everywhere else, which leaves both exactly as they were. */
  siteName?: string
}

const SITE_URL = 'https://theracketcenter.com'
// PNG, not SVG: the backend's /og renderers already reference `/og-default.png`
// and most unfurlers refuse to render an SVG card. public/og-default.png is the
// 1200x630 raster of public/og-default.svg (the SVG stays as the source art).
const DEFAULT_OG_IMAGE = '/og-default.png'

function safeJsonLd(block: JsonLd): string {
  return JSON.stringify(block).replace(/</g, '\\u003c')
}

/**
 * Drop index.html's `data-default-seo` baseline tags once a route supplies its
 * own. Those defaults exist purely for consumers that never run JS; React 19
 * hoists this component's tags by APPENDING them to <head> (verified against a
 * headless render of the built app — only <title> is prepended, so it wins on
 * its own), which would leave two `description` / `og:*` tags per page with the
 * generic one first. Parsers that read the first occurrence of a property would
 * then show the site default instead of the page's own. Idempotent, so
 * StrictMode's double-invoke and repeated route changes are both safe.
 *
 * The static <title> is intentionally NOT marked: it stays as the tab label for
 * the handful of signed-in app routes that render no SEOHead.
 */
function useDropBaselineHead(): void {
  useEffect(() => {
    document.head.querySelectorAll('[data-default-seo]').forEach((node) => node.remove())
  }, [])
}

export function SEOHead({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  jsonLd,
  noindex = false,
  siteName,
}: SEOHeadProps) {
  useDropBaselineHead()

  const fullTitle = buildPageTitle(title, siteName)
  const ogSiteName = String(siteName || '').trim() || SITE_NAME
  const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : undefined
  // og:image must be an absolute URL — site-relative /public assets get the
  // origin prefixed; callers passing an already-absolute CDN URL pass through.
  const ogImageUrl = ogImage.startsWith('/') ? `${SITE_URL}${ogImage}` : ogImage
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImageUrl} />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:site_name" content={ogSiteName} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      {blocks.map((block, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {safeJsonLd(block)}
        </script>
      ))}
    </>
  )
}
